import {
  BackButton,
  useToast,
  useUnsavedChangesBlocker,
} from '@airship-platform/airship-patterns';
import {
  DescriptionEditable,
  ManageProductSection,
} from '@airship-platform/airship-proposal';
import {
  EmptyState,
  FlexBox,
  LoadingButton,
  UnsavedChangesDialog,
} from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { useAppointmentProposalOptionSOWs } from '../../data-access/appointmentProposalOptionSows';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { describeOfflineSyncError } from '../../patterns';
import ROUTE_PATHS from '../../routes/paths';

const SOWFormSchema = z.object({
  summary: z.string(),
});

type SOWFormValues = z.infer<typeof SOWFormSchema>;

const EditSOWPage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { appointmentProposalOptionSowId } = useParams<{
    appointmentProposalOptionSowId: string;
  }>();
  const { currentOption } = useAppointmentProposalOptions();
  const { proposalSows, updateAppointmentProposalOptionSOW, isLoading } =
    useAppointmentProposalOptionSOWs();

  const sow = proposalSows.find(
    (s) => s.appointmentProposalOptionSOWId === appointmentProposalOptionSowId
  );

  const { control, handleSubmit, formState, getValues, reset } =
    useForm<SOWFormValues>({
      defaultValues: { summary: sow?.summary ?? '' },
      resolver: zodResolver(SOWFormSchema),
    });

  const { isBlocked, handleDiscard, handleClose } = useUnsavedChangesBlocker({
    hasChanges: formState.isDirty,
  });

  if (!sow || !currentOption) {
    return (
      <EmptyState
        title="Statement of work not found"
        subtitle="The requested statement of work could not be located or is unavailable for this appointment."
      />
    );
  }

  const saveSOW = () =>
    updateAppointmentProposalOptionSOW(
      sow.appointmentId,
      sow.appointmentProposalId,
      sow.appointmentProposalOptionId,
      sow.appointmentProposalOptionSOWId,
      {
        title: sow.title,
        summary: getValues('summary'),
        shortDescription: sow.shortDescription,
      }
    );

  const onSubmit = () =>
    saveSOW().then(() => {
      showToast('Statement of work updated', 'success');
      reset({ summary: getValues('summary') });
      navigate(
        ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId: currentOption.appointmentId,
          appointmentProposalId: currentOption.appointmentProposalId,
          appointmentProposalOptionId:
            currentOption.appointmentProposalOptionId,
        })
      );
    });

  const isReadOnly = currentOption.isReadOnly;

  return (
    <FlexBox flexDirection={'column'} gap={2} p={2}>
      <Typography variant="h4">Update Statement of Work</Typography>
      <ManageProductSection
        product={{
          name: sow.title,
          images: [],
          error: describeOfflineSyncError(sow.offlineSyncErrors),
        }}
      >
        <FlexBox gap={2} justifyContent={'space-between'}>
          <BackButton
            color="softGold"
            size={'large'}
            sx={{ minWidth: '8rem' }}
            defaultPath="../"
          >
            Cancel
          </BackButton>
          <LoadingButton
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
            size={'large'}
            color={formState.isDirty ? 'primary' : 'softGold'}
            disabled={isReadOnly || isLoading}
            sx={{ minWidth: '8rem' }}
          >
            Update
          </LoadingButton>
        </FlexBox>
        <Divider />
        <Controller
          name="summary"
          control={control}
          render={({ field }) => (
            <DescriptionEditable
              limit={5000}
              title="Description"
              description={field.value}
              onUpdateDescription={field.onChange}
              isEditable={!isReadOnly}
            />
          )}
        />
      </ManageProductSection>
      <UnsavedChangesDialog
        open={isBlocked}
        onLeave={handleDiscard}
        onCancel={handleClose}
      />
    </FlexBox>
  );
};

export default EditSOWPage;
